// Wire-format types and API contract for Disease Code Management (PRD 08).
// Mirrors the shape used by `medicalStaffApi` so the adapter/error patterns stay
// consistent across features.

export type DiseaseCodeStatus = 'ACTIVE' | 'INACTIVE';

export const DISEASE_CODE_STATUSES: readonly DiseaseCodeStatus[] = ['ACTIVE', 'INACTIVE'] as const;

export interface DiseaseGroup {
  id: number;
  name: string;
  // ICD-10 starting code, uppercase (e.g. 'F00').
  rangeStart: string;
  // ICD-10 ending code, uppercase (e.g. 'F99').
  rangeEnd: string;
  description: string | null;
  // Provided by BE so the left panel renders "N코드" without a per-group N+1 fetch.
  codeCount: number;
}

export interface DiseaseCode {
  id: number;
  groupId: number;
  // ICD-10 code, uppercase (e.g. 'F32').
  code: string;
  name: string;
  description: string | null;
  status: DiseaseCodeStatus;
}

// Back-reference entry (PRD §3.2) — a PRO item or deployment module linked to a code.
export interface DiseaseCodeReferenceItem {
  // e.g. 'P-0001' (PRO item) or 'M-0002' (deployment module).
  serial: string;
  name: string;
}

export interface DiseaseCodeReferences {
  proItems: DiseaseCodeReferenceItem[];
  deploymentModules: DiseaseCodeReferenceItem[];
}

export interface CreateDiseaseGroupInput {
  name: string;
  rangeStart: string;
  rangeEnd: string;
  description?: string | null;
}

export type UpdateDiseaseGroupInput = Partial<CreateDiseaseGroupInput>;

export interface CreateDiseaseCodeInput {
  code: string;
  name: string;
  description?: string | null;
  status: DiseaseCodeStatus;
}

export type UpdateDiseaseCodeInput = Partial<CreateDiseaseCodeInput>;

// Returned in `error.meta` when `RANGE_OVERLAP` is raised so the form can show
// which existing group is blocking the new range.
export interface RangeOverlapMeta {
  conflictGroupId: number;
  conflictGroupName: string;
  conflictRangeStart: string;
  conflictRangeEnd: string;
}

// Returned in `error.meta` when `CODE_HAS_LINKED_REFERENCES` is raised so the
// delete-blocked modal can render the PRD §8 guide with real counts.
export interface LinkedReferencesMeta {
  proItemCount: number;
  moduleCount: number;
}

export type DiseaseCodeApiErrorCode =
  | 'NAME_REQUIRED'
  | 'RANGE_REQUIRED'
  | 'RANGE_INVALID'
  | 'RANGE_OVERLAP'
  // Group range [rangeStart, rangeEnd] is not contained in a single ICD-10
  // chapter band (BA decision B). Includes fields.rangeStart / fields.rangeEnd.
  | 'RANGE_OUT_OF_CHAPTER'
  | 'CODE_REQUIRED'
  // Present-but-malformed code (fails ^[A-Z][0-9]{2}$) — distinct from CODE_REQUIRED (empty).
  | 'CODE_FORMAT_INVALID'
  // Well-formed code that falls outside the owning group's [rangeStart, rangeEnd] band.
  | 'CODE_OUT_OF_RANGE'
  | 'CODE_DUPLICATE'
  | 'CODE_HAS_LINKED_REFERENCES'
  // Field-length caps (BE @Size; tokens mirror the admin-api ErrorCode names).
  | 'DISEASE_GROUP_NAME_TOO_LONG'
  | 'DISEASE_GROUP_DESCRIPTION_TOO_LONG'
  | 'DISEASE_NAME_TOO_LONG'
  | 'DISEASE_CODE_DESCRIPTION_TOO_LONG'
  | 'NOT_FOUND'
  | 'INSUFFICIENT_PERMISSION'
  // Transport failure (offline / DNS / timeout) — no HTTP response was received.
  | 'NETWORK'
  | 'UNKNOWN';

export class DiseaseCodeApiError extends Error {
  readonly code: DiseaseCodeApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: DiseaseCodeApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'DiseaseCodeApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, DiseaseCodeApiError.prototype);
  }
}

export interface DiseaseCodeApi {
  // PRD §3.1 — full list, unpaginated. `codeCount` is included per group so the
  // left panel renders without an N+1 fetch.
  listGroups(): Promise<DiseaseGroup[]>;
  createGroup(input: CreateDiseaseGroupInput): Promise<DiseaseGroup>;
  updateGroup(id: number, input: UpdateDiseaseGroupInput): Promise<DiseaseGroup>;
  // Cascade delete (matches prototype): all child codes are removed by BE.
  deleteGroup(id: number): Promise<void>;

  // PRD §3.1 — full list for the selected group, unpaginated. Search is client-side.
  listCodes(groupId: number): Promise<DiseaseCode[]>;
  createCode(groupId: number, input: CreateDiseaseCodeInput): Promise<DiseaseCode>;
  // PRD §3.1 — also used for the status toggle ({ status }).
  updateCode(id: number, input: UpdateDiseaseCodeInput): Promise<DiseaseCode>;
  // PRD §8 — fails with `CODE_HAS_LINKED_REFERENCES` if PRO items / modules link to it.
  deleteCode(id: number): Promise<void>;

  // PRD §3.2 — back-reference panel data for the clicked code.
  getCodeReferences(id: number): Promise<DiseaseCodeReferences>;
}
